/**
 * Chat Management View — dedicated conversation management interface.
 * Modeled after ClipboardManager: list, search, preview, delete, click-to-open.
 */
import { useState, useEffect, useCallback } from 'react';
import { useChatStore } from '../stores/chatStore';
import { conversationsApi, Conversation } from '../api/conversations';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

interface ChatManageViewProps {
  onViewChange: (view: string) => void;
}

export default function ChatManageView({ onViewChange }: ChatManageViewProps) {
  const { loadConversation } = useChatStore();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [previewConversation, setPreviewConversation] = useState<Conversation | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const LIMIT = 20;

  // Load conversations on mount
  const loadPage = useCallback(async (pageOffset: number, append: boolean = false) => {
    try {
      setIsLoading(true);
      const data = await conversationsApi.listConversations(LIMIT, pageOffset);
      if (append) {
        setConversations(prev => [...prev, ...data]);
      } else {
        setConversations(data);
      }
      setHasMore(data.length === LIMIT);
    } catch (error) {
      console.error('Failed to load conversations:', error);
      toast.error('Failed to load conversations');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPage(0);
  }, [loadPage]);

  // Search filter (client-side, debounced)
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset pagination when search changes
  useEffect(() => {
    if (debouncedSearch) {
      setOffset(0);
    }
  }, [debouncedSearch]);

  const filteredConversations = conversations.filter(conv => {
    if (!debouncedSearch) return true;
    const title = (conv.title || 'Untitled Chat').toLowerCase();
    return title.includes(debouncedSearch.toLowerCase());
  });

  // Delete conversation (with confirmation)
  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Delete this conversation? It will be moved to trash.')) return;
    setDeletingId(id);
    try {
      await conversationsApi.deleteConversation(id);
      setConversations(prev => prev.filter(c => c.id !== id));
      toast.success('Conversation deleted');
      // Refresh sidebar conversation list
      useChatStore.getState().loadConversations();
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      toast.error('Failed to delete conversation');
    } finally {
      setDeletingId(null);
    }
  };

  // Click to open conversation in chat view
  const handleOpen = async (id: string) => {
    try {
      await loadConversation(id);
      onViewChange('chat');
    } catch (error) {
      console.error('Failed to load conversation:', error);
      toast.error('Failed to open conversation');
    }
  };

  // Preview conversation messages in modal
  const handlePreview = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const fullConversation = await conversationsApi.getConversation(id);
      setPreviewConversation(fullConversation);
    } catch (error) {
      console.error('Failed to load conversation preview:', error);
      toast.error('Failed to load preview');
    }
  };

  // Load more
  const handleLoadMore = () => {
    const newOffset = offset + LIMIT;
    setOffset(newOffset);
    loadPage(newOffset, true);
  };

  // Close preview modal on ESC
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPreviewConversation(null);
    };
    if (previewConversation) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [previewConversation]);

  return (
    <div role="region" aria-label="Conversation management" className="flex-1 flex flex-col bg-dark-bg overflow-hidden">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-dark-border">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl sm:text-2xl font-bold text-dark-text">Manage Conversations</h1>
          <button
            onClick={() => onViewChange('chat')}
            className="px-3 py-1.5 text-sm bg-dark-card border border-dark-border text-dark-text-muted hover:text-dark-text hover:border-accent-blue rounded-lg transition-all"
          >
            Back to Chat
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-dark-text-muted">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            type="text"
            placeholder="Search conversations..."
            aria-label="Search conversations"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-dark-card border border-dark-border rounded-lg text-dark-text placeholder-dark-text-muted focus:outline-none focus:border-accent-blue transition-colors"
          />
        </div>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {isLoading && conversations.length === 0 ? (
          // Loading skeleton
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-dark-card border border-dark-border rounded-lg p-4 animate-pulse">
                <div className="h-5 bg-dark-border rounded w-3/4 mb-3" />
                <div className="h-3 bg-dark-border rounded w-1/2 mb-2" />
                <div className="h-3 bg-dark-border rounded w-full" />
              </div>
            ))}
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="text-center py-12 text-dark-text-muted">
            {debouncedSearch ? 'No conversations match your search.' : 'No conversations yet.'}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filteredConversations.map((conv) => (
                <div
                  key={conv.id}
                  onClick={() => handleOpen(conv.id)}
                  className="bg-dark-card border border-dark-border rounded-lg p-4 cursor-pointer hover:border-accent-blue transition-all group"
                >
                  {/* Title */}
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-dark-text font-medium truncate flex-1 mr-2">
                      {conv.title || 'Untitled Chat'}
                    </h3>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {/* Preview button */}
                      <button
                        onClick={(e) => handlePreview(conv.id, e)}
                        className="p-1.5 text-dark-text-muted hover:text-accent-blue rounded transition-colors"
                        title="Preview messages"
                        aria-label="Preview conversation messages"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </button>
                      {/* Delete button */}
                      <button
                        onClick={(e) => handleDelete(conv.id, e)}
                        disabled={deletingId === conv.id}
                        className="p-1.5 text-dark-text-muted hover:text-red-400 rounded transition-colors"
                        title="Delete conversation"
                        aria-label="Delete conversation"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Date and metadata */}
                  <div className="flex items-center gap-3 text-xs text-dark-text-muted mb-2">
                    <span>{(() => { try { return format(new Date(conv.updated_at || conv.created_at), 'MMM d, yyyy h:mm a'); } catch { return 'Unknown date'; } })()}</span>
                    {conv.response_verbosity === 'detailed' && (
                      <span className="px-1.5 py-0.5 bg-orange-700/20 text-orange-300 rounded text-[10px]">Detail</span>
                    )}
                  </div>

                  {/* Message count indicator */}
                  <p className="text-xs text-dark-text-muted">
                    Click to open conversation
                  </p>
                </div>
              ))}
            </div>

            {/* Search limitation notice */}
            {debouncedSearch && hasMore && (
              <p className="text-center text-xs text-dark-text-muted mt-3">
                Showing results from loaded conversations only. Load more to search additional conversations.
              </p>
            )}

            {/* Load More */}
            {hasMore && (
              <div className="text-center mt-6">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoading}
                  className="px-4 py-2 bg-dark-card border border-dark-border text-dark-text-muted hover:text-dark-text hover:border-accent-blue rounded-lg transition-all"
                >
                  {isLoading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Preview Modal */}
      {previewConversation && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewConversation(null)}
        >
          <div
            className="bg-dark-card border border-dark-border rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-dark-border">
              <h2 className="text-lg font-semibold text-dark-text truncate">
                {previewConversation.title || 'Untitled Chat'}
              </h2>
              <button
                onClick={() => setPreviewConversation(null)}
                className="p-1 text-dark-text-muted hover:text-dark-text rounded"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body — Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {previewConversation.messages && previewConversation.messages.length > 0 ? (
                previewConversation.messages.map((msg, index) => (
                  <div key={msg.id || `msg-${index}`} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                    <span className="text-[10px] text-dark-text-muted mb-1">
                      {msg.role === 'user' ? 'You' : 'AI'}
                    </span>
                    <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                      msg.role === 'user'
                        ? 'bg-accent-blue/20 text-dark-text'
                        : 'bg-dark-bg text-dark-text'
                    }`}>
                      {(msg.content || '').length > 500
                        ? (msg.content || '').slice(0, 500) + '...'
                        : msg.content || ''
                      }
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-dark-text-muted text-center py-8">No messages in this conversation.</p>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-dark-border flex justify-end gap-2">
              <button
                onClick={() => {
                  handleOpen(previewConversation.id);
                  setPreviewConversation(null);
                }}
                className="px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/80 transition-colors text-sm"
              >
                Open Conversation
              </button>
              <button
                onClick={() => setPreviewConversation(null)}
                className="px-4 py-2 bg-dark-bg text-dark-text-muted rounded-lg hover:text-dark-text transition-colors text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
